import type { ScoringConfidence, ScoringResult } from '@piccolohealth/pbs-common';
import { useDebouncedCallback } from '@piccolohealth/ui';
import { P } from '@piccolohealth/util';
import React from 'react';

const DEFAULT_SEARCH_FILTER = '';
const DEFAULT_LABEL_FILTER: string[] = [];
const DEFAULT_CONFIDENCE_FILTER: ScoringConfidence[] = [];
const DEFAULT_RESULT_FILTER: ScoringResult[] = [];

interface ScoringResultsFilterState {
  searchFilter: string;
  labelFilter: string[];
  confidenceFilter: ScoringConfidence[];
  resultFilter: ScoringResult[];
}

export interface ScoringResultsFilter {
  searchFilter: string;
  labelFilter: string[];
  confidenceFilter: ScoringConfidence[];
  resultFilter: ScoringResult[];
  activeFilterCount: number;
  onSearchFilter: (value: string) => void;
  onLabelFilter: (labels: string[]) => void;
  onConfidenceFilter: (confidence: ScoringConfidence[]) => void;
  onResultFilter: (results: ScoringResult[]) => void;
  reset: () => void;
}

export const useScoringResultsFilter = (): ScoringResultsFilter => {
  const [filters, setFilters] = React.useState<ScoringResultsFilterState>({
    searchFilter: DEFAULT_SEARCH_FILTER,
    labelFilter: DEFAULT_LABEL_FILTER,
    confidenceFilter: DEFAULT_CONFIDENCE_FILTER,
    resultFilter: DEFAULT_RESULT_FILTER,
  });

  const onSearchFilter = useDebouncedCallback(
    (value: string) =>
      setFilters((prev) => ({
        ...prev,
        searchFilter: value,
      })),
    500,
  );

  const onLabelFilter = React.useCallback((labels: string[]) => {
    setFilters((prev) => ({
      ...prev,
      labelFilter: labels,
    }));
  }, []);

  const onConfidenceFilter = React.useCallback((confidence: ScoringConfidence[]) => {
    setFilters((prev) => ({
      ...prev,
      confidenceFilter: confidence,
    }));
  }, []);

  const onResultFilter = React.useCallback((results: ScoringResult[]) => {
    setFilters((prev) => ({
      ...prev,
      resultFilter: results,
    }));
  }, []);

  const reset = React.useCallback(() => {
    setFilters({
      searchFilter: DEFAULT_SEARCH_FILTER,
      labelFilter: DEFAULT_LABEL_FILTER,
      confidenceFilter: DEFAULT_CONFIDENCE_FILTER,
      resultFilter: DEFAULT_RESULT_FILTER,
    });
  }, []);

  const activeFilterCount = P.compact([
    filters.searchFilter.length > 0 ? filters.searchFilter : null,
    filters.labelFilter.length > 0 ? filters.labelFilter : null,
    filters.confidenceFilter.length > 0 ? filters.confidenceFilter : null,
    filters.resultFilter.length > 0 ? filters.resultFilter : null,
  ]).length;

  return {
    searchFilter: filters.searchFilter,
    labelFilter: filters.labelFilter,
    confidenceFilter: filters.confidenceFilter,
    resultFilter: filters.resultFilter,
    activeFilterCount,
    onSearchFilter,
    onLabelFilter,
    onConfidenceFilter,
    onResultFilter,
    reset,
  };
};
